import { endOfDay, endOfMonth, endOfWeek, startOfDay, startOfMonth, startOfWeek } from 'date-fns';
import { fromZonedTime, getTimezoneOffset, toZonedTime } from 'date-fns-tz';

type DateType = Date | number | string;

export function calcZonedDate<
  // eslint-disable-next-line
  T extends (date: DateType, options?: any) => Date,
  O = T extends (date: DateType, options?: infer U) => Date ? U : undefined,
>(date: DateType, tz: string, fn: T, options?: O): Date {
  const inputZoned = getTimeInZone(date as Date, tz);
  const fnZoned = fn(inputZoned, options);
  const fromZonedTimeDate = fromZonedTime(fnZoned, tz);

  if (!isValidDate(fromZonedTimeDate)) {
    const timeZoneOffset = getTimezonedOffsetFromShortTzName(tz);
    console.log('Invalid date after applying function, falling back to offset calculation.');
    const fixedFromZonedTimeDate = fromZonedTime(inputZoned, timeZoneOffset);
    console.log('Fixed from zoned time date:', fixedFromZonedTimeDate);
    return fixedFromZonedTimeDate;
  }

  return fromZonedTimeDate;
}

export function getTimeZoneOffsetFromTimezone(timeZone: string): number {
  return getTimezoneOffset(timeZone, new Date());
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function getZonedEndOfDay(date: Date, timeZone: string) {
  return calcZonedDate(date, timeZone, endOfDay);
}

export function getZonedEndOfWeek(date: Date, timeZone: string) {
  return calcZonedDate(date, timeZone, endOfWeek, { weekStartsOn: 1 });
}

export function getZonedEndOfMonth(date: Date, timeZone: string) {
  return calcZonedDate(date, timeZone, endOfMonth);
}
export function getZonedStartOfDay(date: Date, timeZone: string) {
  return calcZonedDate(date, timeZone, startOfDay);
}
export function getZonedTodayStartOfDay(timeZone: string) {
  return calcZonedDate(new Date(), timeZone, startOfDay);
}

export function getZonedStartOfWeek(date: Date, timeZone: string) {
  return calcZonedDate(date, timeZone, startOfWeek, { weekStartsOn: 1 });
}

export function getZonedStartOfMonth(date: Date, timeZone: string) {
  return calcZonedDate(date, timeZone, startOfMonth);
}

export function getZonedDayOfWeek(date: Date, timeZone: string) {
  return getTimeInZone(date, timeZone).getDay();
}

export function convertISO8601ToSeconds(durationInStr: string): number {
  const match = durationInStr.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) {
    throw new Error(`Invalid duration string: ${durationInStr}`);
  }
  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const seconds = parseInt(match[3] || '0', 10);
  return hours * 3600 + minutes * 60 + seconds;
}

export function isValidDate(d: Date): boolean {
  return d instanceof Date && !isNaN(d.getTime());
}

export function getTimezonedOffsetFromShortTzName(timeZone: string): string {
  const match = timeZone.match(/(GMT|UTC)([+-]\d{1,2})(?::?(\d{2}))?/);
  if (match) {
    const hours = parseInt(match[2], 10);
    const minutes = match[3] ? parseInt(match[3], 10) : 0;
    console.log('Parsed timezone offset:', hours, 'hours and', minutes, 'minutes');
    const sign = hours >= 0 ? '+' : '-';
    const absHours = Math.abs(hours);
    return `${sign}${absHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  return '';
}
export function getTimeInZone(date: Date, timeZone: string): Date {
  const zonedDate = toZonedTime(date, timeZone);
  if (isValidDate(zonedDate)) {
    return zonedDate;
  }

  console.log('Invalid date for timezone:', timeZone, 'falling back to offset calculation.');
  const timeZoneOffset = getTimezonedOffsetFromShortTzName(timeZone);
  console.log('Calculated timezone offset:', timeZoneOffset);
  const fixedZonedDate = toZonedTime(date, timeZoneOffset);
  return fixedZonedDate;
}

export function getStartOfHalfHour(date: Date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes() < 30 ? 0 : 30,
    0,
    0
  );
}
